import { computed, type Ref } from 'vue';
import Fuse from 'fuse.js';
import { groupBy } from '@tools/helpers';
import { type ProductMaatschappijInfo, type VerrijktDocument, type ProductGroep } from '../types';

/**
 * Composable voor het verwerken en groeperen van verzekeringskaarten
 */
export function useVerzekeringskaarten(
  verzekeringskaarten: Ref<
    Array<{ productmaatschappijId: string; bestandsnaam: string; url: string }> | undefined
  >,
  productenMetMaatschappijen: Ref<
    | Array<{
        naam: string;
        productmaatschappijen: Array<{
          productmaatschappijId: string;
          naam: string;
          logoUrl?: string | null;
        }>;
      }>
    | undefined
  >,
  zoekterm: Ref<string>,
) {
  const productmaatschappijLookup = computed(() =>
    bouwProductMaatschappijLookup(productenMetMaatschappijen.value),
  );

  const verrijkteDocumenten = computed(() =>
    verrijkDocumentenMetProductInfo(verzekeringskaarten.value, productmaatschappijLookup.value),
  );

  const gefilterdeDocumenten = computed(() =>
    filterDocumentenOpZoekterm(verrijkteDocumenten.value, zoekterm.value),
  );

  const gegroepeerdeKaarten = computed(() =>
    groepeerDocumentenPerProductEnMaatschappij(gefilterdeDocumenten.value),
  );

  const heeftResultaten = computed(() => gegroepeerdeKaarten.value.length > 0);

  return {
    gegroepeerdeKaarten,
    heeftResultaten,
  };
}

/**
 * Bouwt een lookup map van productmaatschappijId naar product/maatschappij informatie
 */
function bouwProductMaatschappijLookup(
  producten:
    | Array<{
        naam: string;
        productmaatschappijen: Array<{
          productmaatschappijId: string;
          naam: string;
          logoUrl?: string | null;
        }>;
      }>
    | undefined,
): Map<string, ProductMaatschappijInfo> {
  const lookup = new Map<string, ProductMaatschappijInfo>();

  (producten ?? []).forEach((product) => {
    product.productmaatschappijen.forEach((pm) => {
      lookup.set(pm.productmaatschappijId, {
        productNaam: product.naam,
        maatschappijNaam: pm.naam,
        logoUrl: pm.logoUrl ?? null,
      });
    });
  });

  return lookup;
}

/**
 * Verrijkt verzekeringskaarten met product en maatschappij informatie
 */
function verrijkDocumentenMetProductInfo(
  kaarten: Array<{ productmaatschappijId: string; bestandsnaam: string; url: string }> | undefined,
  lookup: Map<string, ProductMaatschappijInfo>,
): VerrijktDocument[] {
  return (kaarten ?? [])
    .map((kaart) => {
      const pmInfo = lookup.get(kaart.productmaatschappijId);
      if (!pmInfo) return null;

      return {
        ...pmInfo,
        bestandsnaam: kaart.bestandsnaam,
        url: kaart.url,
      };
    })
    .filter((doc): doc is VerrijktDocument => doc !== null);
}

/**
 * Filtert documenten op basis van zoekterm met fuzzy search
 */
function filterDocumentenOpZoekterm(
  documenten: VerrijktDocument[],
  term: string,
): VerrijktDocument[] {
  if (term.length < 2) return documenten;

  const fuse = new Fuse(documenten, {
    keys: ['productNaam', 'maatschappijNaam', 'bestandsnaam'],
    threshold: 0.3,
  });

  return fuse.search(term).map((result) => result.item);
}

/**
 * Groepeert documenten per product en maatschappij
 */
function groepeerDocumentenPerProductEnMaatschappij(
  documenten: VerrijktDocument[],
): ProductGroep[] {
  const perProduct = groupBy(documenten, (doc) => doc.productNaam);

  return perProduct
    .map(({ key: productNaam, items: productDocumenten }) => {
      const perMaatschappij = groupBy(productDocumenten, (doc) => doc.maatschappijNaam);

      const maatschappijen = perMaatschappij
        .map(({ key: maatschappijNaam, items: maatschappijDocumenten }) => ({
          maatschappijNaam,
          logoUrl: maatschappijDocumenten[0]?.logoUrl ?? null,
          documenten: maatschappijDocumenten.map((doc) => ({
            bestandsnaam: doc.bestandsnaam,
            url: doc.url,
          })),
        }))
        .toSorted((a, b) => a.maatschappijNaam.localeCompare(b.maatschappijNaam));

      return {
        productNaam,
        maatschappijen,
        totaalAantalDocumenten: maatschappijen.reduce((sum, m) => sum + m.documenten.length, 0),
      };
    })
    .toSorted((a, b) => a.productNaam.localeCompare(b.productNaam));
}
